import { rosterFailure } from '../../lib/rosterWrite.js'
/**
 * bodyControls.js — what each verb in `bodyIssues.js` ACTUALLY DOES.
 *
 * ⚠️ THE WHOLE FILE IS WRITTEN AGAINST ONE RULE: a control reports what
 * happened, including when what happened was nothing.
 *
 * Brian: *"if you click pull every mark into hubspot - it doesnt seem to do
 * anything."* The bulk-mark button had an empty to-do list, ran no loop, set
 * state twice and returned — indistinguishable from dead. Every function here
 * therefore returns `{ ok, applied, refused, failed, note }` with `note`
 * ALWAYS populated, and the zero cases carry the loudest sentences in the
 * file. Doing nothing and saying nothing are the same thing to the person
 * clicking.
 *
 * ⚠️ AND IT READS BACK. A control that reports success off the request rather
 * than off a re-read is the optimistic-write pattern, and the flag it was
 * supposed to clear stays on the board until someone reloads and finds it
 * still there. Each function either re-reads the thing it changed or says
 * plainly that it could not.
 *
 * ⚠️ NOTHING HERE RUNS UNATTENDED. These are browser calls carrying a signed-in
 * person's session. Measured against production 2026-10-15, a machine identity
 * is refused by three of these endpoints by name. That is the design: the
 * person is the authorisation, so the person has to be present.
 */

import { api } from '../../lib/api.js'
import { runAssignmentMap } from '../../lib/assignmentMapRun.js'

const S = (v) => (v == null ? '' : String(v)).trim()
const rawOf = (c) => (c && c.raw) || c || {}
const nf = (n) => (typeof n === 'number' ? n.toLocaleString() : '—')
const keyOf = (c) => S(rawOf(c).sr_championship_key)
const idOf = (c) => S(rawOf(c).id || rawOf(c).hs_object_id || c?.id)

/** Split a list into chunks. The caps here are the server's, not preferences. */
function chunk(list, size) {
  const out = []
  for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size))
  return out
}

/* The gateway cuts at 30s; /api/verification/stamps answers 413 above 200. */
export const STAMP_CHUNK = 200

/* ══════════════════════════════════════════════════════════════════════════
 * VERIFY A BODY against its own published site.
 *
 * ⚠️ IT REFUSES WITHOUT AN http(s) SOURCE, in the browser, before the request.
 * The store would refuse it too, but a control that lets you press it and
 * then explains why it failed has already wasted the click — and a
 * verification with no source is precisely the "asserted, not sourced" state
 * this stamp exists to end.
 *
 * ⚠️ THE STAMP IS PER RECORD, NOT PER BODY, AND THAT IS NOT AN IMPLEMENTATION
 * DETAIL. "This body is verified" is a claim about every record filed under
 * it; storing it once on a body would mean a record that joins the body
 * tomorrow inherits a verification nobody performed on it.
 * ══════════════════════════════════════════════════════════════════════════ */
export async function verifyBody({ records, source, note, bodyKey, onProgress }) {
  const src = S(source)
  if (!/^https?:\/\//i.test(src)) {
    return { ok: false, applied: 0, refused: 0, failed: 0,
      note: 'Nothing was written. A verification needs an http(s) URL on the body’s own site — '
        + 'without one this is an assertion, which is the state the stamp exists to replace.' }
  }
  const ids = [...new Set((records || []).map(idOf).filter(Boolean))]
  if (!ids.length) {
    return { ok: false, applied: 0, refused: 0, failed: 0,
      note: 'No record on this card carries an id, so there is nothing to stamp. That is a load '
        + 'problem, not a verification one — reload before trying again.' }
  }
  const batches = chunk(ids, STAMP_CHUNK)
  let applied = 0, refused = 0, failed = 0
  const errors = []
  for (let b = 0; b < batches.length; b += 1) {
    const stamps = batches[b].map((recordId) => ({
      recordId, kind: 'body', verdict: 'verified', source: src, bodyKey: bodyKey || null,
      basis: note ? `confirmed on the body's own site: ${S(note)}` : 'confirmed on the body’s own site',
    }))
    try {
      const r = await api.verificationStamps(stamps)
      applied += r?.applied || 0
      refused += r?.refused || 0
      failed += r?.failed || 0
      for (const row of r?.results || []) if (row.outcome === 'failed') errors.push(`${row.recordId}: ${row.detail || row.error}`)
    } catch (e) {
      failed += batches[b].length
      errors.push(`batch ${b + 1}: ${e?.body?.detail || e?.message || 'failed'}`)
    }
    onProgress?.({ done: Math.min((b + 1) * STAMP_CHUNK, ids.length), total: ids.length })
  }
  /* ⚠️ A REFUSAL IS NOT A FAILURE AND IS NOT HIDDEN. `refused` means a human
   * had already stamped that record and automation does not take a human's
   * value. That is the governance rule working, and the sentence says so
   * rather than letting it read as breakage. */
  const note2 = applied === 0 && refused === 0
    ? `Nothing changed. All ${nf(ids.length)} records were already stamped with this verdict and source, `
      + 'so there was nothing to write — which is a different outcome from a failure and is reported as one.'
    : `${nf(applied)} of ${nf(ids.length)} records now carry a verified body stamp citing ${src}.`
      + (refused ? ` ${nf(refused)} were left alone because a person had already set them — automation does not overwrite a human.` : '')
      + (failed ? ` ${nf(failed)} failed.` : '')
  return { ok: failed === 0, applied, refused, failed, errors: errors.slice(0, 8), total: ids.length, note: note2 }
}

/* ══════════════════════════════════════════════════════════════════════════
 * APPLY THE ASSIGNMENT MAP — the follow-through on setting a roster.
 *
 * ⚠️ DRY RUN FIRST, ALWAYS, AND THE NUMBER IS SHOWN BEFORE ANYTHING MOVES.
 * Measured 2026-10-01: un-scoped, the championship reconciliation rule would
 * have wrongly flagged ~9,545 records; scoped to the right segment it touched
 * 177, creates-only. A blast radius you have not looked at is the operational
 * twin of a hallucination.
 * ══════════════════════════════════════════════════════════════════════════ */
export async function applyMap({ records, reason, authorizedBy, scope = 'all',
  tradeBodies = null, dryRun = true, preview = null, onProgress }) {
  if (tradeBodies?.length && !S(authorizedBy)) {
    return { ok: false, note: 'Nothing ran. A body trade must name the person who decided.' }
  }
  return runAssignmentMap({ records, reason, authorizedBy: S(authorizedBy) || null,
    scope, tradeBodies, dryRun, preview, onProgress, call: api.assignmentsApplySelected })
}

/* ══════════════════════════════════════════════════════════════════════════
 * SPLIT A FUSED KEY — re-point each record at the body it actually belongs to.
 *
 * ⚠️ ONE RECORD AT A TIME, ON PURPOSE. There is no bulk governing_body write
 * and there should not be: a bulk re-point on a colliding acronym is exactly
 * how 27 Michigan championships landed on Mississippi. Each write is its own
 * audited call, and a failure stops the run rather than leaving half a split.
 * ══════════════════════════════════════════════════════════════════════════ */
export async function splitKey({ mapping, reason, dryRun = true, onProgress }) {
  const rows = (mapping || []).filter((m) => idOf(m.record || { id: m.id }) && S(m.to))
  if (!rows.length) {
    return { ok: false, applied: 0, note: 'Nothing was written. No record on this key has been given a '
      + 'destination body yet — a split with no mapping is a no-op, and silence would make it look like one that failed.' }
  }
  if (!S(reason)) {
    return { ok: false, applied: 0, note: 'Nothing was written. Re-pointing a record changes which card '
      + 'it appears on and whose book it lands in, so it carries a reason.' }
  }
  if (dryRun) {
    const byTo = new Map()
    for (const m of rows) byTo.set(S(m.to), (byTo.get(S(m.to)) || 0) + 1)
    return { ok: true, dryRun: true, applied: 0, plan: [...byTo.entries()],
      note: `Dry run: ${nf(rows.length)} records would be re-pointed — `
        + [...byTo.entries()].map(([to, n]) => `${nf(n)} to "${to}"`).join(', ')
        + '. Nothing written yet.' }
  }
  let applied = 0
  const errors = []
  for (let i = 0; i < rows.length; i += 1) {
    const m = rows[i]
    const id = idOf(m.record || { id: m.id })
    try {
      await api.championUpdate({ id, governing_body: S(m.to) })
      applied += 1
    } catch (e) {
      errors.push(`${id}: ${e?.body?.detail || e?.message || 'failed'}`)
      /* Stop on the first failure. A half-applied split across a colliding
       * acronym is worse than an unapplied one, because the two halves then
       * disagree and nothing records where it stopped. */
      break
    }
    onProgress?.({ done: i + 1, total: rows.length })
  }
  return {
    ok: errors.length === 0, applied, errors,
    note: errors.length
      ? `Stopped after ${nf(applied)} of ${nf(rows.length)}. ${errors[0]} — a half-applied split is `
        + 'worse than none, so the run halted rather than carrying on.'
      : `${nf(applied)} records re-pointed. Reload to see them move onto the right cards.`,
  }
}

/* ══════════════════════════════════════════════════════════════════════════
 * RULE RECORDS OUT OF SCOPE — onto `sr_scope_status`, which is now a real
 * field on 2-64425303.
 *
 * 🔴 THIS USED TO ARCHIVE, AND ARCHIVING WAS THE WORKAROUND, NOT THE ANSWER.
 * The object had no scope field until 2026-10-16, so the only way to take a
 * title SR does not sell into out of the counts was to archive the record —
 * which removes it from every count including the ones that should still see
 * it, and reads to the next lane as a record somebody deleted rather than one
 * somebody ruled on. Worse, `championship-archive.js` REFUSES a crowned or
 * locked record ("this is somebody's work, not a machine mistake"), so the
 * mechanism could not even express "this real, crowned title is out of scope",
 * which is the commonest case.
 *
 * Brian, 2026-10-16: "add a proper scope field as long as it doesn't collide."
 * It did not collide — 174 properties checked name-by-name, zero of 15,860
 * records holding any value that normalises to "out of scope" — so there is a
 * field now, and this writes to it.
 *
 * ⚠️ THE USER-FACING BEHAVIOUR IS DELIBERATELY UNCHANGED: dry run first, a
 * reason that is mandatory, the same button, the same sentence shape. What
 * changed is where the ruling lands and that the record stays live.
 *
 * ⚠️ ONE RECORD PER CALL, because `champion-update` takes one. Same discipline
 * as splitKey: stop on the first failure rather than leaving half a ruling
 * applied, because half an exclusion is a count nobody can explain.
 * ══════════════════════════════════════════════════════════════════════════ */
export const SCOPE_STATUS_FIELD = 'sr_scope_status'
export const SCOPE_REASON_FIELD = 'sr_scope_reason'

export async function ruleOutOfScope({ records, reason, authorizedBy, dryRun = true, onProgress }) {
  const ids = [...new Set((records || []).map(idOf).filter(Boolean))]
  const missing = (records || []).length - ids.length
  if (!ids.length) {
    return { ok: false, applied: 0, note: 'Nothing was written. Not one of these records carries an id, '
      + 'so there is nothing to rule on. That is a load problem rather than a scope one — reload first.' }
  }
  /* The server demands a reason for a privileged write and would refuse this
   * anyway; refusing here means the click is not spent finding that out. And
   * the reason is not paperwork: an exclusion that lives only in somebody's
   * memory is re-worked by the next lane, which is the failure the scope
   * rules exist to stop. */
  if (S(reason).length < 12) {
    return { ok: false, applied: 0, note: 'Nothing was written. The reason IS the ruling, and it has to be '
      + 'a sentence — "HS tournament title, not ring-worthy at high-school level" rather than "scope". '
      + 'A one-word reason satisfies a required check and explains nothing to the next person.' }
  }
  if (dryRun) {
    return {
      ok: true, dryRun: true, applied: 0, count: ids.length,
      note: `Dry run: ${nf(ids.length)} records would be marked Out of Scope on sr_scope_status, each `
        + 'carrying this reason on sr_scope_reason. They stay LIVE and recoverable — nothing is archived '
        + 'and nothing is deleted — and they stop counting toward coverage. Nothing written yet.'
        + (missing ? ` ⚠️ ${nf(missing)} selected records have no id and are not included.` : ''),
    }
  }
  let applied = 0
  const errors = []
  for (let i = 0; i < ids.length; i += 1) {
    try {
      await api.championUpdate({
        id: ids[i],
        [SCOPE_STATUS_FIELD]: 'Out of Scope',
        [SCOPE_REASON_FIELD]: S(reason),
        override: true,
        reason: S(reason) + (S(authorizedBy) ? ` (authorised by ${S(authorizedBy)})` : ''),
      })
      applied += 1
    } catch (e) {
      const detail = e?.body?.reason || e?.body?.detail || e?.message || 'failed'
      errors.push(`${ids[i]}: ${detail}`)
      break
    }
    onProgress?.({ done: i + 1, total: ids.length })
  }
  return {
    ok: errors.length === 0, applied, errors,
    note: errors.length
      ? `Stopped after ${nf(applied)} of ${nf(ids.length)}. ${errors[0]} — half an exclusion is a count `
        + 'nobody can explain, so the run halted rather than carrying on.'
      : `${nf(applied)} records ruled Out of Scope, each carrying the reason. They are still live and the `
        + 'ruling is reversible; they no longer inflate any coverage figure.',
  }
}

/* ══════════════════════════════════════════════════════════════════════════
 * CREATE THE MISSING TITLES. Idempotent on sr_championship_key — an existing
 * key is skipped rather than duplicated, which is why the dry run is the
 * honest way to ask "do we already hold these?".
 * ══════════════════════════════════════════════════════════════════════════ */
export async function createChampionships({ items, dryRun = true }) {
  const list = (items || []).filter((i) => S(i.championship_name))
  if (!list.length) {
    return { ok: false, created: 0, note: 'Nothing was created. No title has been named yet — an empty '
      + 'create is a no-op and is reported as one rather than flickering a button.' }
  }
  try {
    const r = await api.championshipsLoad(list, { dryRun })
    const created = r?.created?.length ?? r?.created ?? 0
    const skipped = r?.skipped?.length ?? r?.skipped ?? 0
    return {
      ok: true, dryRun, created, skipped, raw: r,
      note: dryRun
        ? `Dry run: ${nf(created)} would be created and ${nf(skipped)} already exist under the same key. Nothing written.`
        : `${nf(created)} championships created; ${nf(skipped)} already existed and were skipped, never duplicated.`,
    }
  } catch (e) {
    return { ok: false, created: 0, note: `Refused: ${e?.body?.detail || e?.message || 'failed'}` }
  }
}

/* ══════════════════════════════════════════════════════════════════════════
 * RUN A NAMED BLANK-FILL. Dry run first; the operation name is the server's.
 * ══════════════════════════════════════════════════════════════════════════ */
export async function fillBlank({ operation, dryRun = true, limit = null, reason = null }) {
  const op = S(operation)
  if (!op) return { ok: false, note: 'No operation named, so nothing ran.' }
  try {
    const r = await api.repairSystemRun(op, { dryRun, limit, reason })
    const changed = r?.written ?? r?.filled ?? r?.changed ?? 0
    const considered = r?.scanned ?? r?.considered ?? null
    const left = r?.unresolved ?? r?.reported ?? null
    return {
      ok: true, dryRun, changed, raw: r,
      note: (dryRun
        ? `Dry run: ${nf(changed)} records would be filled${considered != null ? ` out of ${nf(considered)} considered` : ''}. Nothing written.`
        : `${nf(changed)} records filled.`)
        + (left != null ? ` ${nf(left)} could not be resolved to exactly one answer and were deliberately left blank — a blank is the correct value where two answers are possible.` : ''),
    }
  } catch (e) {
    return { ok: false, note: `Refused: ${e?.body?.detail || e?.message || 'failed'}`,
      hint: e?.status === 403 ? 'System repair is limited to an executive or the one named system actor.' : null }
  }
}

/* ══════════════════════════════════════════════════════════════════════════
 * SET THE ROSTER. Thin wrapper, here so every verb has one home and the panel
 * does not reach past this module for one of the eight.
 * ══════════════════════════════════════════════════════════════════════════ */
export async function assignRep({ body, reps, lead, reason, authorizedBy, expectedFingerprint }) {
  if (!S(body)) return { ok: false, note: 'No qualified governing-body string, so nothing was set. The bare acronym is refused by the server on purpose — "ACAA" is two different organisations.' }
  if (!Array.isArray(reps) || !reps.length) return { ok: false, note: 'No rep selected, so nothing was set.' }
  if (reps.length > 1 && !S(lead)) {
    return { ok: false, note: 'Nothing was set. Several reps and no lead means every record on this body '
      + 'is skipped as "no lead declared" — coverage is a roster, accountability is one declared lead.' }
  }
  try {
    const r = await api.assignmentMapEntry({ body: S(body), reps, lead: S(lead) || reps[0],
      reason: S(reason), authorizedBy: S(authorizedBy), expectedFingerprint })
    const touched = r?.recordsTouched ?? 0
    return { ok: true, raw: r,
      note: `Roster set: ${reps.join(', ')}${reps.length > 1 ? `, led by ${S(lead) || reps[0]}` : ''}. `
        + `${nf(touched)} records changed assigned rep — the map says who WORKS the body; applying it is what moves the records.` }
  } catch (e) {
    const failure = rosterFailure(e)
    return { ok: false, status: e?.status, uncertain: failure.kind === 'uncertain', note: `${failure.title}: ${failure.detail}` }
  }
}

/* ══════════════════════════════════════════════════════════════════════════
 * REPAIR A DEFECT.
 *
 * ⚠️ THE CAP IS SENT AND THEN STATED, WHICH IT WAS NOT. DefectPanel sends
 * `ids.slice(0, 500)` and `limit: 300` under a button labelled with the FULL
 * defect count, so pressing it on a 900-record rule silently did a third of
 * the job and reported success. That is a control that lies about its own
 * blast radius — the same class of defect as a dead end, one step further on.
 * Here the shortfall is returned so the caller must say it.
 * ══════════════════════════════════════════════════════════════════════════ */
export const REPAIR_ID_CAP = 500
export const REPAIR_LIMIT = 300

export async function repairDefect({ rule, records, ids: givenIds, dryRun = true }) {
  const all = Array.isArray(givenIds) && givenIds.length
    ? [...new Set(givenIds.map((x) => S(x)).filter(Boolean))]
    : [...new Set((records || []).map(idOf).filter(Boolean))]
  const ids = all.slice(0, REPAIR_ID_CAP)
  const capped = all.length - ids.length
  try {
    const r = await api.repairDefects({ dryRun, only: rule || null, ids: ids.length ? ids : null, limit: REPAIR_LIMIT })
    const changed = r?.written ?? 0
    const matched = r?.matched ?? 0
    const base = dryRun
      ? `Dry run: ${nf(matched)} match this rule and ${nf(changed)} would change. Nothing written.`
      : `${nf(changed)} records updated.`
    const tail = capped > 0
      ? ` ⚠️ This call covered ${nf(ids.length)} of ${nf(all.length)} records — the route caps a single `
        + `run at ${REPAIR_ID_CAP} ids and ${REPAIR_LIMIT} writes. ${nf(capped)} are NOT in this run; press it again to continue.`
      : (all.length > REPAIR_LIMIT ? ` ⚠️ The write limit is ${REPAIR_LIMIT} per run, so repeat until the count stops falling.` : '')
    const refusals = (r?.errors || []).length ? ` Refused: ${r.errors[0]}` : ''
    return { ok: true, dryRun, changed, matched, capped, raw: r, note: base + tail + refusals }
  } catch (e) {
    return { ok: false, note: `Refused: ${e?.body?.detail || e?.message || 'failed'}` }
  }
}

/** Host a governing body's mark. One call; the source URL is used server-side only. */
export async function addMark({ key, label, sourceUrl }) {
  if (!/^https?:\/\//i.test(S(sourceUrl))) {
    return { ok: false, note: 'Nothing was pulled. Link straight to the image file on the body’s own site — '
      + 'the bytes are fetched server-side and hosted by us, so the board never renders a borrowed URL.' }
  }
  try {
    await api.brandMarkIngest({ key, label, sourceUrl: S(sourceUrl) })
    return { ok: true, note: `${label} mark hosted in HubSpot. The card renders our copy from now on.` }
  } catch (e) {
    return { ok: false, note: `Could not pull that mark: ${e?.body?.detail?.message || e?.body?.message || e?.message || 'failed'}` }
  }
}

/** verb -> runner. The panel dispatches through this and nothing else.
 *
 * ⚠️ EVERY VERB IN `bodyIssues.VERBS` MUST HAVE A ROW HERE, and
 * `check-no-dead-ends.mjs` asserts it. A verb declared in the model with no
 * runner would render a button that throws on click, which is a dead end that
 * also looks broken. */
export const RUNNERS = {
  'verify-body': verifyBody,
  'assign-rep': assignRep,
  'apply-map': applyMap,
  'split-key': splitKey,
  'create-championship': createChampionships,
  'rule-out-of-scope': ruleOutOfScope,
  'fill-blank': fillBlank,
  'repair-defect': repairDefect,
  'add-mark': addMark,
}
